/**
 * Leer un Excel (.xlsx) o un CSV EN EL NAVEGADOR — `OD-0057` D-07.
 *
 * El fichero no sale del ordenador: se lee aquí, y a la API sólo van las filas
 * como texto (D-07: «el fichero NO se guarda»). Por eso no hace falta subirlo
 * entero ni guardarlo en ninguna parte.
 *
 * ```text
 * XLSX    un ZIP con XML dentro. Se lee la PRIMERA hoja, con sus textos
 *         compartidos y sus estilos: el estilo dice si un numero es una FECHA
 *         (Excel guarda los dias desde 1899) o un PORCENTAJE (21 % es 0,21)
 * CSV     separado por ; , o tabulador, el que tenga la cabecera. UTF-8, y si
 *         no lo es, Windows-1252, que es lo que guarda Excel en Espana
 * NUMEROS Excel guarda 176 como 176.00000000000003. Se redondea a cuatro
 *         decimales EN TEXTO, con enteros: el dinero nunca pasa por un
 *         number de JavaScript
 * ```
 *
 * Sin dependencias: el ZIP se descomprime con `DecompressionStream`, que traen
 * todos los navegadores actuales.
 */

export type Hoja = {
  readonly cabeceras: readonly string[];
  /** Cada fila con su número en la hoja (la cabecera es la 1). */
  readonly filas: ReadonlyArray<{ readonly numero: number; readonly celdas: readonly string[] }>;
};

export type ErrorDeLectura = "FORMATO_NO_ADMITIDO" | "FICHERO_ILEGIBLE" | "SIN_FILAS";

// ------------------------------------------------------------- posiciones

/**
 * Un entero sin signo escrito en el XML: una posición de fila o de columna, el
 * número de un estilo, un texto compartido o un día de Excel. NUNCA un importe:
 * los importes se quedan en texto y los valida la API (la web no calcula
 * dinero, `web-shell.mjs`). Lo que no es un entero da `null`.
 */
export function posicion(texto: string | null | undefined, base = 10): number | null {
  if (texto === null || texto === undefined || texto === "") return null;
  const cifras = "0123456789abcdef".slice(0, base);
  let n = 0;
  for (const ch of texto.toLowerCase()) {
    const d = cifras.indexOf(ch);
    if (d < 0) return null;
    n = n * base + d;
  }
  return n;
}

// ------------------------------------------------------------------- ZIP

const u16 = (b: Uint8Array, i: number): number => b[i]! | (b[i + 1]! << 8);
const u32 = (b: Uint8Array, i: number): number => (b[i]! | (b[i + 1]! << 8) | (b[i + 2]! << 16) | (b[i + 3]! << 24)) >>> 0;

async function inflar(datos: Uint8Array): Promise<Uint8Array> {
  const flujo = new Blob([datos as BlobPart]).stream().pipeThrough(new DecompressionStream("deflate-raw"));
  return new Uint8Array(await new Response(flujo).arrayBuffer());
}

/** Los ficheros de un ZIP, por nombre. Lee el directorio central: vale con o sin descriptores de datos. */
async function leerZip(b: Uint8Array): Promise<Map<string, Uint8Array>> {
  let fin = -1;
  for (let i = b.length - 22; i >= Math.max(0, b.length - 65_557); i--) {
    if (u32(b, i) === 0x06054b50) {
      fin = i;
      break;
    }
  }
  if (fin < 0) throw new Error("ZIP sin directorio");
  const entradas = u16(b, fin + 10);
  let p = u32(b, fin + 16);
  const out = new Map<string, Uint8Array>();
  const nombres = new TextDecoder();
  for (let k = 0; k < entradas; k++) {
    if (u32(b, p) !== 0x02014b50) throw new Error("ZIP roto");
    const metodo = u16(b, p + 10);
    const comprimido = u32(b, p + 20);
    const largoNombre = u16(b, p + 28);
    const largoExtra = u16(b, p + 30);
    const largoComentario = u16(b, p + 32);
    const local = u32(b, p + 42);
    const nombre = nombres.decode(b.subarray(p + 46, p + 46 + largoNombre));
    const inicio = local + 30 + u16(b, local + 26) + u16(b, local + 28);
    const crudo = b.subarray(inicio, inicio + comprimido);
    if (metodo === 0) out.set(nombre, crudo);
    else if (metodo === 8) out.set(nombre, await inflar(crudo));
    p += 46 + largoNombre + largoExtra + largoComentario;
  }
  return out;
}

// ------------------------------------------------------------------- XML

function desescapar(s: string): string {
  return s.replace(/&(#x[0-9a-fA-F]+|#\d+|amp|lt|gt|quot|apos);/g, (_, e: string) => {
    if (e === "amp") return "&";
    if (e === "lt") return "<";
    if (e === "gt") return ">";
    if (e === "quot") return '"';
    if (e === "apos") return "'";
    const n = e.startsWith("#x") ? posicion(e.slice(2), 16) : posicion(e.slice(1));
    return n === null || n > 0x10ffff ? "" : String.fromCodePoint(n);
  });
}

/** Todo el texto de los `<t>` de un trozo (un texto con formato son varios). */
function textoDe(xml: string): string {
  return [...xml.matchAll(/<t(?:\s[^>]*)?>([\s\S]*?)<\/t>/g)].map((m) => desescapar(m[1] ?? "")).join("");
}

const atributo = (etiqueta: string, nombre: string): string | null => etiqueta.match(new RegExp(`\\s${nombre}="([^"]*)"`))?.[1] ?? null;

function columna(ref: string): number {
  let n = 0;
  for (const ch of ref.replace(/\d+$/, "")) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n - 1;
}

type Estilo = "FECHA" | "PORCENTAJE" | null;

/** Qué es cada estilo de celda: los formatos de fecha y de porcentaje, de serie o propios. */
function estilos(xml: string | null): Estilo[] {
  if (xml === null) return [];
  const propios = new Map<number, string>();
  for (const m of xml.matchAll(/<numFmt\s[^>]*>/g)) {
    const id = posicion(atributo(m[0], "numFmtId"));
    if (id !== null) propios.set(id, desescapar(atributo(m[0], "formatCode") ?? ""));
  }
  const xfs = xml.match(/<cellXfs[^>]*>([\s\S]*?)<\/cellXfs>/)?.[1] ?? "";
  return [...xfs.matchAll(/<xf\s[^>]*?\/?>/g)].map((m) => {
    const id = posicion(atributo(m[0], "numFmtId")) ?? 0;
    if (id === 9 || id === 10) return "PORCENTAJE";
    if ((id >= 14 && id <= 22) || (id >= 45 && id <= 47)) return "FECHA";
    const codigo = (propios.get(id) ?? "").replace(/"[^"]*"|\[[^\]]*\]/g, "");
    if (codigo.includes("%")) return "PORCENTAJE";
    if (/[dmy]/i.test(codigo) && !/[#0]/.test(codigo)) return "FECHA";
    return null;
  });
}

// --------------------------------------------------------------- números

/**
 * El número que guarda Excel, como texto decimal y redondeado a cuatro
 * decimales (half-up), sin pasar por `number`: `"176.00000000000003"` ->
 * `"176"`, `"1.5E-2"` -> `"0.015"`. `escala` mueve la coma (2 = por cien).
 */
export function numeroDeExcel(crudo: string, escala = 0): string | null {
  const m = crudo.trim().match(/^(-?)(\d*)(?:\.(\d*))?(?:[eE]([+-]?\d+))?$/);
  if (m === null || (m[2] === "" && (m[3] ?? "") === "")) return null;
  const negativo = m[1] === "-";
  let digitos = `${m[2] ?? ""}${m[3] ?? ""}`.replace(/^0+(?=\d)/, "");
  const exp = posicion((m[4] ?? "0").replace(/^[+-]/, "")) ?? 0;
  let decimales = (m[3] ?? "").length - ((m[4] ?? "").startsWith("-") ? -exp : exp) - escala;
  // Se lleva a diezmilésimas: entero = valor * 10^4.
  let entero: bigint;
  if (decimales <= 4) {
    entero = BigInt(digitos === "" ? "0" : digitos) * 10n ** BigInt(4 - decimales);
  } else {
    const sobran = decimales - 4;
    if (digitos.length <= sobran) digitos = digitos.padStart(sobran + 1, "0");
    const base = BigInt(digitos.slice(0, digitos.length - sobran));
    const siguiente = posicion(digitos[digitos.length - sobran]) ?? 0;
    entero = base + (siguiente >= 5 ? 1n : 0n);
    decimales = 4;
  }
  const s = entero.toString().padStart(5, "0");
  const fraccion = s.slice(-4).replace(/0+$/, "");
  const parte = s.slice(0, -4);
  const texto = fraccion === "" ? parte : `${parte}.${fraccion}`;
  return negativo && entero !== 0n ? `-${texto}` : texto;
}

/** Días desde el 30-12-1899 (así cuenta Excel, con su 29-02-1900 incluido) a `AAAA-MM-DD`. */
export function fechaDeExcel(crudo: string): string | null {
  const dias = posicion(crudo.trim().match(/^(\d+)(?:\.\d+)?$/)?.[1]);
  if (dias === null) return null;
  const d = new Date(Date.UTC(1899, 11, 30) + dias * 86_400_000);
  return Number.isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
}

// ------------------------------------------------------------------ XLSX

async function leerXlsx(bytes: Uint8Array): Promise<string[][]> {
  const zip = await leerZip(bytes);
  const txt = (n: string): string | null => {
    const b = zip.get(n);
    return b === undefined ? null : new TextDecoder().decode(b);
  };
  // La PRIMERA hoja del libro, por su relación; si no se encuentra, sheet1.
  let ruta = "xl/worksheets/sheet1.xml";
  const libro = txt("xl/workbook.xml");
  const relaciones = txt("xl/_rels/workbook.xml.rels");
  const primera = libro?.match(/<sheet\s[^>]*>/)?.[0];
  const rid = primera === undefined ? null : atributo(primera, "r:id");
  if (rid !== null && relaciones !== null) {
    for (const m of relaciones.matchAll(/<Relationship\s[^>]*>/g)) {
      if (atributo(m[0], "Id") !== rid) continue;
      const destino = atributo(m[0], "Target") ?? "";
      ruta = destino.startsWith("/") ? destino.slice(1) : `xl/${destino}`;
    }
  }
  const hoja = txt(ruta);
  if (hoja === null) throw new Error("sin hoja");
  const compartidos = [...(txt("xl/sharedStrings.xml") ?? "").matchAll(/<si>([\s\S]*?)<\/si>/g)].map((m) => textoDe(m[1] ?? ""));
  const tipos = estilos(txt("xl/styles.xml"));

  const filas: string[][] = [];
  for (const fila of hoja.matchAll(/<row\b([^>]*)>([\s\S]*?)<\/row>/g)) {
    const r = posicion(atributo(`<row${fila[1] ?? ""}>`, "r")) ?? filas.length + 1;
    const celdas: string[] = [];
    for (const c of (fila[2] ?? "").matchAll(/<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
      const cabeza = `<c${c[1] ?? ""}>`;
      const ref = atributo(cabeza, "r");
      const i = ref === null ? celdas.length : columna(ref);
      const t = atributo(cabeza, "t");
      const cuerpo = c[2] ?? "";
      const v = cuerpo.match(/<v>([\s\S]*?)<\/v>/)?.[1];
      let valor = "";
      if (t === "s") valor = compartidos[posicion(v) ?? -1] ?? "";
      else if (t === "inlineStr") valor = textoDe(cuerpo);
      else if (t === "str" || t === "e") valor = desescapar(v ?? "");
      else if (t === "b") valor = v === "1" ? "VERDADERO" : "FALSO";
      else if (v !== undefined) {
        const estilo = tipos[posicion(atributo(cabeza, "s")) ?? 0] ?? null;
        valor = (estilo === "FECHA" ? fechaDeExcel(v) : numeroDeExcel(v, estilo === "PORCENTAJE" ? 2 : 0)) ?? v;
      }
      celdas[i] = valor.trim();
    }
    filas[r - 1] = Array.from(celdas, (x) => x ?? "");
  }
  return Array.from(filas, (x) => x ?? []);
}

// ------------------------------------------------------------------- CSV

function decodificar(bytes: Uint8Array): string {
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes).replace(/^﻿/, "");
  } catch {
    return new TextDecoder("windows-1252").decode(bytes);
  }
}

export function leerCsv(texto: string): string[][] {
  const primera = texto.split(/\r?\n/, 1)[0] ?? "";
  const separador = [";", "\t", ","].map((s) => [s, primera.split(s).length] as const).sort((a, b) => b[1] - a[1])[0]![0];
  const filas: string[][] = [];
  let fila: string[] = [];
  let celda = "";
  let comillas = false;
  for (let i = 0; i < texto.length; i++) {
    const ch = texto[i]!;
    if (comillas) {
      if (ch === '"' && texto[i + 1] === '"') {
        celda += '"';
        i++;
      } else if (ch === '"') comillas = false;
      else celda += ch;
    } else if (ch === '"') comillas = true;
    else if (ch === separador) {
      fila.push(celda.trim());
      celda = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && texto[i + 1] === "\n") i++;
      fila.push(celda.trim());
      filas.push(fila);
      fila = [];
      celda = "";
    } else celda += ch;
  }
  if (celda !== "" || fila.length > 0) {
    fila.push(celda.trim());
    filas.push(fila);
  }
  return filas;
}

// ------------------------------------------------------------------ todo

/** Lee el fichero y devuelve la cabecera y las filas con datos. */
export async function leerHoja(nombre: string, bytes: Uint8Array): Promise<Hoja | ErrorDeLectura> {
  const n = nombre.toLowerCase();
  let crudas: string[][];
  try {
    if (n.endsWith(".xlsx")) crudas = await leerXlsx(bytes);
    else if (n.endsWith(".csv") || n.endsWith(".txt")) crudas = leerCsv(decodificar(bytes));
    else return "FORMATO_NO_ADMITIDO";
  } catch {
    return "FICHERO_ILEGIBLE";
  }
  const indice = crudas.findIndex((f) => f.some((c) => c !== ""));
  if (indice < 0) return "SIN_FILAS";
  const cabeceras = crudas[indice]!.map((c) => c.trim());
  const filas = crudas
    .slice(indice + 1)
    .map((celdas, k) => ({ numero: indice + k + 2, celdas: cabeceras.map((_, i) => (celdas[i] ?? "").trim()) }))
    .filter((f) => f.celdas.some((c) => c !== ""));
  if (filas.length === 0) return "SIN_FILAS";
  return { cabeceras, filas };
}
